import { Marketplaces } from 'common/constants/enums';
import { ReactNode } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { v4 } from 'uuid';

import amazonLogoUrl from '../../../../assets/images/logos/amazon-icon.png';
import walmartLogoUrl from '../../../../assets/images/logos/walmart-icon.png';

export const MARKETPLACE_LOGOS: Record<Marketplaces, string> = {
  [Marketplaces.Amazon]: amazonLogoUrl,
  [Marketplaces.Walmart]: walmartLogoUrl,
};

export const MARKETPLACE_BASE_URLS: Record<Marketplaces, string> = {
  [Marketplaces.Amazon]: 'https://www.amazon.com/dp/',
  [Marketplaces.Walmart]: 'https://www.walmart.com/ip/',
};

export interface MarketplaceOption {
  label: string;
  value: Marketplaces;
}

export const MARKETPLACE_OPTIONS: MarketplaceOption[] = [
  { label: 'Amazon', value: Marketplaces.Amazon },
  { label: 'Walmart', value: Marketplaces.Walmart },
];

export const formatMarketplaceOptionLabel = (option: MarketplaceOption) => (
  <div style={{ alignItems: 'center', display: 'flex', gap: '8px' }}>
    <img
      alt={option.label}
      src={MARKETPLACE_LOGOS[option.value]}
      style={{ height: 'auto', width: '16px' }}
    />
    <span>{option.label}</span>
  </div>
);

export interface ExportRegionOption {
  label: string;
  value: string;
}

export const EXPORT_REGION_OPTIONS: ExportRegionOption[] = [
  { label: 'United States', value: 'us' },
  { label: 'United Kingdom', value: 'uk' },
  { label: 'Germany', value: 'de' },
  { label: 'France', value: 'fr' },
  { label: 'Italy', value: 'it' },
  { label: 'Spain', value: 'es' },
  { label: 'Canada', value: 'ca' },
  { label: 'Japan', value: 'jp' },
  { label: 'Australia', value: 'au' },
];

export const formatExportRegionOptionLabel = (option: ExportRegionOption) => (
  <div style={{ alignItems: 'center', display: 'flex', gap: '8px' }}>
    <ReactCountryFlag
      countryCode={option.value === 'uk' ? 'GB' : option.value.toUpperCase()}
      style={{ fontSize: '1.2em' }}
      svg
    />
    <span>{option.label}</span>
  </div>
);

export interface ITagCreationForm {
  tags: {
    itemID: string;
    tag: string;
  }[];
  publisherID: number;
  publisherCountry?: string;
  marketplace: Marketplaces;
  date: Date;
}

export const TAG_CREATION_FORM_DEFAULTS: Partial<ITagCreationForm> = {
  marketplace: Marketplaces.Amazon,
  tags: [{ itemID: '', tag: '' }],
};

export const buildTemporaryLink = (
  itemID: string,
  params: string,
  marketplace: Marketplaces,
) => {
  const search = new URLSearchParams(params);
  const base = marketplace === Marketplaces.Amazon
    ? 'https://www.amazon.com/dp'
    : 'https://www.walmart.com/ip';

  return `${base}/${itemID}?${search.toString()}`;
};

/**
 * Extracts query-string parameters from either a raw param string (standard mode)
 * or a full URL (advanced mode) and returns them as numbered display lines.
 */
export const renderLinkParams = (value: string, isAdvanced: boolean): ReactNode[] => {
  let searchString = value;

  if (isAdvanced) {
    try {
      const url = new URL(value);
      searchString = url.search.replace(/^\?/, '');
    } catch {
      return [<span key={v4()}>{value}</span>];
    }
  }

  const data = new URLSearchParams(searchString);
  const items: ReactNode[] = [];
  let count = 1;

  data.forEach((val, key) => {
    items.push(
      <span key={v4()}>
        {`${count}. ${encodeURIComponent(key)}: ${encodeURIComponent(val)}`}
      </span>,
    );
    count += 1;
  });

  return items;
};

export const resolveLink = (
  itemID: string,
  tag: string,
  marketplace: Marketplaces,
  isAdvanced: boolean,
) => (isAdvanced ? tag : buildTemporaryLink(itemID, tag, marketplace));
